import React, { useState, useEffect, useCallback } from 'react';
import { X, UserPlus, Users, Crown, Shield, User, Trash2 } from 'lucide-react';
import { Team, TeamMember, User as UserType } from '../../types';
import { teamAPI, userAPI } from '../../services/api';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
  onTeamUpdated: () => void;
}

const TeamMemberModal: React.FC<TeamMemberModalProps> = ({
  isOpen,
  onClose,
  team,
  onTeamUpdated
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<'member' | 'lead' | 'admin'>('member');

  const loadTeamMembers = useCallback(async () => {
    try {
      const membersData = await teamAPI.getTeamMembers(team.id);
      setMembers(membersData);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    }
  }, [team.id]);

  useEffect(() => {
    if (isOpen) {
      loadTeamMembers();
      loadUsers();
    }
  }, [isOpen, team.id, loadTeamMembers]);

  const loadUsers = async () => {
    try {
      // Use batch endpoint to reduce API calls
      const response = await api.get('/teams/batch');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setIsLoading(true);
    try {
      await teamAPI.addTeamMember(team.id, {
        user_id: selectedUserId,
        role: selectedRole
      });
      toast.success('Member added to team successfully');
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/admin/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/teams/batch');
      
      loadTeamMembers();
      setIsAddingMember(false);
      setSelectedUserId(null);
      setSelectedRole('member');
    } catch (error: any) {
      console.error('Error adding team member:', error);
      toast.error(error.response?.data?.error || 'Failed to add team member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMemberRole = async (memberId: number, newRole: 'member' | 'lead' | 'admin') => {
    setIsLoading(true);
    try {
      await teamAPI.updateTeamMember(team.id, memberId, { role: newRole });
      toast.success('Member role updated successfully');
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/admin/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/teams/batch');
      
      loadTeamMembers();
    } catch (error: any) {
      console.error('Error updating member role:', error);
      toast.error(error.response?.data?.error || 'Failed to update member role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    setIsLoading(true);
    try {
      await teamAPI.removeTeamMember(team.id, memberId);
      toast.success('Member removed from team successfully');
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/admin/batch');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/teams/batch');
      
      loadTeamMembers();
    } catch (error: any) {
      console.error('Error removing team member:', error);
      toast.error(error.response?.data?.error || 'Failed to remove team member');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4 text-red-600" />;
      case 'lead':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      default:
        return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'lead':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter out users who are already team members
  const availableUsers = users.filter(user => 
    !members.some(member => member.user_id === user.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Team Members
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {team.full_name} - Manage team members and roles
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Add Member Section */}
          {!isAddingMember ? (
            <div className="mb-6">
              <button
                onClick={() => setIsAddingMember(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                <span>Add Member</span>
              </button>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Member</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select User
                  </label>
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as 'member' | 'lead' | 'admin')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="member">Member</option>
                    <option value="lead">Lead</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex items-end space-x-2">
                  <button
                    onClick={handleAddMember}
                    disabled={isLoading || !selectedUserId}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingMember(false);
                      setSelectedUserId(null);
                      setSelectedRole('member');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Current Members ({members.length})
            </h3>
            
            {members.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No members in this team yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(member.role)}
                        <div>
                          <div className="font-medium text-gray-900">
                            {member.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateMemberRole(member.id, e.target.value as 'member' | 'lead' | 'admin')}
                        className={`px-3 py-1 text-sm rounded-full border-0 ${getRoleColor(member.role)}`}
                        disabled={isLoading}
                      >
                        <option value="member">Member</option>
                        <option value="lead">Lead</option>
                        <option value="admin">Admin</option>
                      </select>
                      
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={isLoading}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;
